/**
 * EmployeeView Component
 * 
 * Main view component for employees to access their payslip system.
 * This component handles:
 * - Loading employee information from sessionStorage
 * - Managing navigation between payslip and settings pages
 * - Responsive layout (mobile vs desktop)
 * - Sidebar collapse/expand functionality
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import './EmployeeView.css';
import { OfficeEmpPreview } from '../../layouts/PaySlipPreview/components/OfficeEmp/OfficeEmpPreview';
import { ConstructionEmpPreview } from '../../layouts/PaySlipPreview/components/ConstructionEmp/ConstructionEmpPreview';
import { EmployeeSidebar } from './EmployeeSidebar';
import { EmployeeSettings } from './EmployeeSettings';
import { EmployeeDocuments } from '../../layouts/DocumentDelivery/EmployeeDocuments';
import EmployeeService from '../../services/EmployeeService';
import { getConsentFormConfig, getConsentStatus, submitConsent } from '../../services/ConsentService';
import { ConsentModal } from '../FirstLoginConsent/ConsentModal';
import type { ResolvedConsentFormConfig } from '../FirstLoginConsent/consentFormTypes';
import { buildLocalFallbackConsentConfig } from '../FirstLoginConsent/consentDefaults';

/**
 * Interface defining the structure of an employee object
 */
interface Employee {
  empCode: string;
  name: string;
  userType: string;
  dualAccount?: boolean;
  constructionWorkerName?: string;
}

export const EmployeeView: React.FC = () => {
  // State for storing the currently logged-in employee
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  
  // Loading state to show spinner while fetching employee data
  const [isLoading, setIsLoading] = useState(true);
  
  // Current active page ('payslip' or 'settings')
  const [activePage, setActivePage] = useState('payslip');
  
  // State to control sidebar collapse/expand behavior
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Flag to determine if the current viewport is mobile (width <= 768px)
  const [isMobile, setIsMobile] = useState(false);
  // First-time consent modal: show when user has not yet submitted consent (backend)
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentCheckDone, setConsentCheckDone] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  /** Global consent template from API (fallback if fetch fails). */
  const [consentFormConfig, setConsentFormConfig] = useState<ResolvedConsentFormConfig | null>(null);
  /** When dual office + construction account: which payslip to show */
  const [payslipKind, setPayslipKind] = useState<'office' | 'construction'>('office');

  /**
   * Effect hook to load employee data from sessionStorage on component mount.
   * The employee data is stored under the 'currentEmployee' key after login.
   */
  useEffect(() => {
    // Get current employee from sessionStorage
    const storedEmployee = sessionStorage.getItem('currentEmployee');
    if (storedEmployee) {
      const employee = JSON.parse(storedEmployee);
      setCurrentEmployee(employee);
    }
    setIsLoading(false);
  }, []);

  /**
   * Effect: fetch consent status from backend; show modal only when user has not yet consented (first login).
   * Only show modal when API succeeds and hasConsented is false. On API failure, do not show modal (avoid blocking user).
   */
  useEffect(() => {
    if (!currentEmployee) return;
    let cancelled = false;
    setConsentCheckDone(false);
    getConsentStatus(currentEmployee.empCode, 'office')
      .then((data) => {
        if (!cancelled) {
          setShowConsentModal(Boolean(data.hasConsented === false));
          setConsentCheckDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShowConsentModal(false);
          setConsentCheckDone(true);
        }
      });
    return () => { cancelled = true; };
  }, [currentEmployee]);

  useEffect(() => {
    if (!currentEmployee) return;
    let cancelled = false;
    getConsentFormConfig()
      .then((c) => {
        if (cancelled) return;
        if (!c) {
          setConsentFormConfig(buildLocalFallbackConsentConfig());
          return;
        }
        setConsentFormConfig({
          sections: c.sections,
          printNoticeLines:
            c.printNoticeLines.length >= 2 ? [c.printNoticeLines[0], c.printNoticeLines[1]] : buildLocalFallbackConsentConfig().printNoticeLines,
        });
      })
      .catch(() => {
        if (!cancelled) setConsentFormConfig(buildLocalFallbackConsentConfig());
      });
    return () => {
      cancelled = true;
    };
  }, [currentEmployee]);

  /**
   * Effect hook to detect mobile viewport and update state accordingly.
   * Also sets up a resize listener to handle dynamic window resizing.
   * Mobile breakpoint is set at 768px width.
   */
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    // Check on initial mount
    checkMobile();
    
    // Listen for window resize events
    window.addEventListener('resize', checkMobile);
    
    // Cleanup: remove event listener on unmount
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Handler for navigation page changes.
   * Updates the active page state to switch between payslip and settings views.
   * 
   * @param page - The page identifier ('payslip' or 'settings')
   */
  const handlePageChange = (page: string) => {
    setActivePage(page);
    // Don't automatically collapse sidebar on mobile - let user control it
  };

  /**
   * Toggles the sidebar collapse/expand state.
   * This affects the sidebar width and main content margin on desktop layouts.
   */
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  /**
   * Logout handler that clears employee data from sessionStorage and reloads the page.
   * This will return the user to the login screen.
   */
  const handleLogout = () => {
    EmployeeService.logout();
    window.location.reload(); // Reload to show login
  };

  /**
   * Renders the main content area based on the currently active page.
   * Returns either the payslip preview component or the settings component.
   * 
   * @returns JSX element for the active page content
   */
  const renderMainContent = () => {
    const emp = currentEmployee!;
    const dual = Boolean(emp.dualAccount);

    const payslipBlock = (
      <>
        {dual && (
          <nav className="tab-navigation" role="tablist" aria-label="給与区分">
            <button
              type="button"
              role="tab"
              aria-selected={payslipKind === 'office'}
              className={`tab-button ${payslipKind === 'office' ? 'active' : ''}`}
              onClick={() => setPayslipKind('office')}
            >
              本社
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={payslipKind === 'construction'}
              className={`tab-button ${payslipKind === 'construction' ? 'active' : ''}`}
              onClick={() => setPayslipKind('construction')}
            >
              現場
            </button>
          </nav>
        )}
        <div className="tab-content employee-payslip-tab-content">
          <div className="tab-panel">
            {(!dual || payslipKind === 'office') && (
              <OfficeEmpPreview
                isEmployeeView={true}
                currentEmployeeCode={emp.empCode}
                currentEmployeeName={emp.name}
              />
            )}
            {dual && payslipKind === 'construction' && (
              <ConstructionEmpPreview
                isEmployeeView={true}
                currentEmployeeCode={emp.empCode}
                fallbackConstructionWorkerName={
                  emp.constructionWorkerName?.trim() || emp.name
                }
              />
            )}
          </div>
        </div>
      </>
    );

    switch (activePage) {
      case 'payslip':
        return (
          <div className="tab-container employee-payslip-tab-root">
            <div className="tab-header">
              <h3>給与明細</h3>
              <p>{dual ? '本社・現場の給与明細を切り替えて確認できます' : 'あなたの給与明細を確認できます'}</p>
            </div>
            {payslipBlock}
          </div>
        );
      case 'documents':
        return <EmployeeDocuments />;
      case 'settings':
        return <EmployeeSettings currentEmployee={emp} />;
      default:
        return (
          <div className="tab-container employee-payslip-tab-root">
            <div className="tab-header">
              <h3>給与明細</h3>
              <p>あなたの給与明細を確認できます</p>
            </div>
            {payslipBlock}
          </div>
        );
    }
  };

  // Show loading spinner while employee data is being fetched
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  // Error state: Show error message if employee data is not found in sessionStorage
  // This can happen if the tab was closed or user hasn't logged in
  if (!currentEmployee) {
    return (
      <div className="error-container">
        <h2>エラー</h2>
        <p>従業員情報が見つかりません。再度ログインしてください。</p>
        <button onClick={handleLogout} className="logout-button">
          ログイン画面に戻る
        </button>
      </div>
    );
  }

  const handleConsentAgree = async () => {
    if (!currentEmployee || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: currentEmployee.empCode,
        name: currentEmployee.name,
        empType: 'office',
        status: 'agree',
      });
      if (ok.success) setShowConsentModal(false);
    } finally {
      setConsentSubmitting(false);
    }
  };

  const handleConsentDisagree = async () => {
    if (!currentEmployee || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: currentEmployee.empCode,
        name: currentEmployee.name,
        empType: 'office',
        status: 'disagree',
      });
      if (ok.success) {
        EmployeeService.logout();
        window.location.href = '/login';
      }
    } finally {
      setConsentSubmitting(false);
    }
  };

  /**
   * Mobile Layout Configuration
   * Mobile layout has a vertical stack:
   * - Header at the top
   * - Collapsible sidebar below header
   * - Main content area at the bottom
   */
  const resolvedConsentConfig = consentFormConfig ?? buildLocalFallbackConsentConfig();

  if (isMobile) {
    return (
      <div className="employee-layout mobile-layout">
        {showConsentModal && (
          <ConsentModal
            empCode={currentEmployee.empCode}
            name={currentEmployee.name}
            config={resolvedConsentConfig}
            onAgree={handleConsentAgree}
            onDisagree={handleConsentDisagree}
            submitting={consentSubmitting}
          />
        )}
        {/* Header at the top for mobile layout */}
        <div className="employee-header">
          <div className="header-content">
            <div className="employee-info">
              <h2>給与明細システム</h2>
              <p>ようこそ、{currentEmployee.name}さん</p>
              <p className="employee-code">従業員コード: {currentEmployee.empCode}</p>
            </div>
            <button onClick={handleLogout} className="logout-button">
              🚪 ログアウト
            </button>
          </div>
        </div>
        
        {/* Collapsible sidebar below header */}
        <EmployeeSidebar 
          activePage={activePage} 
          setActivePage={handlePageChange} 
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
        />
        
        {/* Main content area below sidebar */}
        <div className="mobile-content-area">
          {renderMainContent()}
        </div>
      </div>
    );
  }

  /**
   * Desktop Layout Configuration
   * Desktop layout has a horizontal arrangement:
   * - Fixed sidebar on the left (can be collapsed)
   * - Main content area on the right with dynamic margin based on sidebar state
   *   - Collapsed sidebar: 72px margin
   *   - Expanded sidebar: 282px margin
   */
  return (
    <div className="employee-layout">
      {showConsentModal && (
        <ConsentModal
          empCode={currentEmployee.empCode}
          name={currentEmployee.name}
          config={resolvedConsentConfig}
          onAgree={handleConsentAgree}
          onDisagree={handleConsentDisagree}
          submitting={consentSubmitting}
        />
      )}
      {/* Fixed sidebar on the left side */}
      <EmployeeSidebar 
        activePage={activePage} 
        setActivePage={handlePageChange} 
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
      />
      
      {/* Main container that adjusts based on sidebar state */}
      {/* Margin dynamically changes when sidebar is collapsed/expanded for smooth transition */}
      <div 
        className={`main-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        style={{ 
          marginLeft: isSidebarCollapsed ? '72px' : '282px',
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* Header at the top of the main content area */}
        <div className="employee-header">
          <div className="header-content">
            <div className="employee-info">
              <h2>給与明細システム</h2>
              <p>ようこそ、{currentEmployee.name}さん</p>
              <p className="employee-code">従業員コード: {currentEmployee.empCode}</p>
            </div>
            <button onClick={handleLogout} className="logout-button">
              🚪 ログアウト
            </button>
          </div>
        </div>
        
        {/* Main content area below header */}
        <div className="content-area">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};
